import { useCallback, useEffect, useState, type ComponentType } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import HowItWorks from "@/components/HowItWorks";

import DynamicSchemaMarkup from "@/components/DynamicSchemaMarkup";
import WhatsAppButton from "@/components/WhatsAppButton";

import LazyOnView from "@/components/LazyOnView";
import { useSectionTracking } from "@/hooks/useSectionTracking";

const Index = () => {
  useSectionTracking();
  const { t, i18n } = useTranslation();
  const importAvailableVehicle = useCallback(() => import("@/components/AvailableVehicle"), []);
  const importIndexAvailability = useCallback(() => import("@/components/IndexAvailability"), []);
  const importPricing = useCallback(() => import("@/components/Pricing"), []);
  const importTechBundle = useCallback(() => import("@/components/TechBundle"), []);
  const importUseCases = useCallback(() => import("@/components/UseCases"), []);
  const importTrustBundle = useCallback(() => import("@/components/TrustBundle"), []);
  const importLocalSection = useCallback(() => import("@/components/LocalSection"), []);
  const importRelatedServices = useCallback(() => import("@/components/RelatedServices"), []);
  const importFAQ = useCallback(() => import("@/components/FAQ"), []);
  const importContactForm = useCallback(() => import("@/components/ContactForm"), []);
  const importFooter = useCallback(() => import("@/components/Footer"), []);

  // Defer MobileSticky + ExitIntentPopup until browser is idle
  const [MobileSticky, setMobileSticky] = useState<ComponentType | null>(null);
  const [ExitIntentPopup, setExitIntentPopup] = useState<ComponentType | null>(null);
  useEffect(() => {
    const load = () => {
      import("@/components/MobileSticky").then(m => setMobileSticky(() => m.default));
      import("@/components/ExitIntentPopup").then(m => setExitIntentPopup(() => m.default));
    };
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(load, { timeout: 3000 });
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(load, 2000);
      return () => clearTimeout(id);
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background overflow-x-hidden">
      <Helmet>
        <title>{t("index.title")}</title>
        <meta name="description" content={t("index.description")} />
        <link rel="canonical" href="https://frostflotte.de/" />
        <link rel="alternate" hrefLang="de" href="https://frostflotte.de/" />
        <link rel="alternate" hrefLang="en" href="https://frostflotte.de/en" />
        <meta property="og:title" content={t("index.ogTitle")} />
        <meta property="og:description" content={t("index.ogDescription")} />
        <meta property="og:url" content={i18n.language === "en" ? "https://frostflotte.de/en" : "https://frostflotte.de/"} />
      </Helmet>
      <DynamicSchemaMarkup />
      <Header />
      <main className="flex-1 overflow-hidden">
      <Hero />

      {/* 1. Fuhrpark direkt nach dem Hero — die Kaufentscheidung */}
      <LazyOnView importFn={importAvailableVehicle} minHeight="600px" rootMargin="600px" anchorId="fuhrpark" />

      {/* CTA nach dem Fuhrpark wird durch die Kontaktoptionen im Fuhrpark abgedeckt */}

      {/* 1b. Verfügbarkeit & Einsatz — Substanz-Abschnitt oberhalb der Preise */}
      <LazyOnView importFn={importIndexAvailability} minHeight="350px" rootMargin="600px" />

      {/* 2. Preise & Kilometer */}
      <LazyOnView importFn={importPricing} minHeight="500px" rootMargin="500px" anchorId="preise" />


      {/* 3. Ablauf in 3 Schritten */}
      <HowItWorks />

      {/* 4. Technik & Ausstattung (Fakten + technische Daten + Kühlen/Tiefkühlen) */}
      <LazyOnView importFn={importTechBundle} minHeight="800px" rootMargin="400px" anchorId="technische-daten" componentProps={{ vehicleType: "kuehl" }} />

      {/* 5. Einsatzfälle & Branchen */}
      <LazyOnView importFn={importUseCases} minHeight="400px" rootMargin="600px" />


      {/* 6. Vertrauen (Zertifikate + Werte/Kühlkette) */}
      <LazyOnView importFn={importTrustBundle} minHeight="600px" rootMargin="600px" />

      {/* 7. Einsatzgebiet */}
      <LazyOnView importFn={importLocalSection} minHeight="300px" rootMargin="600px" />

      {/* 7b. Weitere Leistungen — interne Verlinkung zu Themenseiten */}
      {i18n.language !== "en" && (
        <LazyOnView importFn={importRelatedServices} minHeight="400px" rootMargin="600px" componentProps={{ currentPath: "/" }} />
      )}

      {/* 8. FAQ */}
      <LazyOnView importFn={importFAQ} minHeight="400px" rootMargin="600px" anchorId="faq" />

      {/* 9. Anfrage */}
      <LazyOnView importFn={importContactForm} minHeight="500px" rootMargin="2000px" anchorId="anfrage" />
      
      </main>
      <div className="mt-auto">
        <LazyOnView importFn={importFooter} minHeight="350px" rootMargin="1200px" />
      </div>
      {MobileSticky && <MobileSticky />}
      {ExitIntentPopup && <ExitIntentPopup />}
      <WhatsAppButton />
    </div>
  );
};

export default Index;
